/**
 * <s-gallery-slider> — Smooth one-image-at-a-time product gallery.
 *
 * The track is a native horizontal scroll-snap row, so touch swipes and
 * trackpads glide natively. This component adds previous/next arrows,
 * thumbnail/dot navigation, arrow-key support and keeps the active
 * thumbnail in sync with the visible slide. Respects reduced motion.
 *
 * Required child markup:
 *   [data-slider-track]   — Scroll-snap row; each direct child is one slide.
 *   [data-slider-prev]    — Optional previous button.
 *   [data-slider-next]    — Optional next button.
 *   [data-slider-thumb]   — Optional buttons with data-index (0-based) that jump to a slide.
 *   [data-slider-expand]  — Optional button toggling [data-expanded] on this element to reveal extra slides.
 */
class SGallerySlider extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #index = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children);
    if (this.#slides.length === 0) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.#index - 1), opts);
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.#index + 1), opts);

    const expand = this.querySelector('[data-slider-expand]');
    expand?.addEventListener(
      'click',
      () => {
        const open = !this.hasAttribute('data-expanded');
        this.toggleAttribute('data-expanded', open);
        expand.setAttribute('aria-expanded', String(open));
        if (!open) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      },
      opts,
    );

    for (const thumb of this.querySelectorAll('[data-slider-thumb]')) {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.index)), opts);
    }

    this.#track.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.#index + (rtl ? -1 : 1));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.#index + (rtl ? 1 : -1));
        }
      },
      opts,
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
        }
      },
      { root: this.#track, threshold: 0.6 },
    );
    for (const slide of this.#slides) this.#observer.observe(slide);

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  go(index) {
    const count = this.#slides.length;
    const next = ((index % count) + count) % count;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slide = this.#slides[next];
    // Scroll only the track horizontally so the page itself never jumps.
    const trackRect = this.#track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    this.#track.scrollBy({ left: slideRect.left - trackRect.left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(next);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    for (const thumb of this.querySelectorAll('[data-slider-thumb]')) {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-gallery-slider')) customElements.define('s-gallery-slider', SGallerySlider);
